import { type Actor } from '@shared/board'
import { type BoardChange } from '@shared/session'
import { type MessageKey } from '@/i18n/messages'

// Board changes between two turns, grouped the way a person reads them:
// by who did them, then by what they did.

const OPS: BoardChange['op'][] = ['added', 'edited', 'changed', 'moved', 'removed']
export const VERB: Record<BoardChange['op'], MessageKey> = {
  added: 'board.added',
  moved: 'board.moved',
  edited: 'board.edited',
  changed: 'board.changed',
  removed: 'board.removed',
}

export type Group = {
  actor: Actor | null
  ops: { op: BoardChange['op']; changes: BoardChange[] }[]
}

export const actorKey = (a: Actor | null) => (a ? `${a.kind}:${a.id}` : '?')

/** Changes by person or session, then by kind of change; the session's own changes left out. */
export function groupChanges(changes: BoardChange[], ownSession: string): Group[] {
  const groups = new Map<string, Group>()
  for (const c of changes) {
    const actor = c.by.at(-1) ?? null
    // The AI's own changes are shown on its previous turn; here they are noise.
    if (actor?.kind === 'session' && actor.id === ownSession) continue
    const key = actorKey(actor)
    const g = groups.get(key) ?? { actor, ops: [] }
    groups.set(key, g)
    let slot = g.ops.find((o) => o.op === c.op)
    if (!slot) {
      slot = { op: c.op, changes: [] }
      g.ops.push(slot)
    }
    // A card moved and edited by the same person reads once, as edited.
    if (!slot.changes.some((x) => x.id === c.id)) slot.changes.push(c)
  }
  for (const g of groups.values()) {
    g.ops.sort((a, b) => OPS.indexOf(a.op) - OPS.indexOf(b.op))
    const edited = new Set(g.ops.find((o) => o.op === 'edited')?.changes.map((c) => c.id))
    for (const o of g.ops)
      if (o.op === 'moved') o.changes = o.changes.filter((c) => !edited.has(c.id))
    g.ops = g.ops.filter((o) => o.changes.length)
  }
  return [...groups.values()].filter((g) => g.ops.length)
}
